/**
 * Account · your own security settings (any signed-in persona).
 *
 * Three self-service controls, email-only (no SMS anywhere):
 *   1. Email two-factor — POST /auth/mfa/email/enable | /auth/mfa/disable. When on, sign-in
 *      requires a 6-digit code delivered to your inbox.
 *   2. Preferred email — prove you control an address: POST /auth/email/verify/send issues a
 *      6-digit code (10-min expiry, 1 per 30s), POST /auth/email/verify/confirm verifies it and
 *      promotes the address to your verified preferred_email.
 *   3. Password — POST /auth/password/change: current password + a new one (≥8 chars). The API
 *      bumps token_version, so every OTHER session is signed out; this one continues on the
 *      fresh token pair the response carries.
 *
 * There is no read endpoint for the current MFA / verification state, so the toggle and the
 * verified badge reflect the actions taken in this session.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.js";
import { getSession, setSession } from "../lib/auth.js";
import { Alert, CheckBig, Eye, EyeOff, ShieldCheck } from "../components/icons.js";
import { apiErrorText } from "./admin/adminUi.js";
import { SettingSwitch } from "./Profile.js";

const EMAIL_RE = /^\S+@\S+\.\S+$/;

export function AccountSecurity(): ReactNode {
  // ── Email two-factor ──────────────────────────────────────────────────────
  const [mfaOn, setMfaOn] = useState(false);
  const enableMfa = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; mfaEnabled: boolean; method: string }>("/auth/mfa/email/enable"),
    onSuccess: () => setMfaOn(true),
  });
  const disableMfa = useMutation({
    mutationFn: () => api.post<{ ok: boolean; mfaEnabled: boolean }>("/auth/mfa/disable"),
    onSuccess: () => setMfaOn(false),
  });
  const mfaBusy = enableMfa.isPending || disableMfa.isPending;
  const mfaError =
    enableMfa.isError || disableMfa.isError
      ? apiErrorText(enableMfa.error ?? disableMfa.error, "Couldn't update two-factor. Please retry.")
      : null;

  // ── Preferred email verification ──────────────────────────────────────────
  const [emailInput, setEmailInput] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null); // "" ⇒ current account email
  const [code, setCode] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = useMutation({
    mutationFn: () => {
      const trimmed = emailInput.trim();
      return api.post<{ ok: boolean }>(
        "/auth/email/verify/send",
        trimmed ? { email: trimmed } : {},
      );
    },
    onSuccess: () => {
      setSentTo(emailInput.trim());
      setCode("");
      setCooldown(30);
    },
  });
  const confirm = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; emailVerified: boolean; email: string }>(
        "/auth/email/verify/confirm",
        { code },
      ),
    onSuccess: (res) => {
      setVerifiedEmail(res.email);
      setSentTo(null);
      setCode("");
    },
  });

  // ── Change password ───────────────────────────────────────────────────────
  // Same ≥8-char + confirm validation the set/reset-password screen uses (SetPassword.tsx).
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [capsOn, setCapsOn] = useState(false);
  // One show/hide for the three password fields (audit 25: no eye toggle, unlike login).
  const [showPw, setShowPw] = useState(false);
  const pwType = showPw ? "text" : "password";
  const changePw = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; accessToken: string; refreshToken: string; tokenType: string }>(
        "/auth/password/change",
        { currentPassword: currentPw, newPassword: newPw },
      ),
    onSuccess: (res) => {
      // The API bumped token_version — the old tokens (this tab's included) are dead. Persist
      // the fresh pair it minted so THIS session continues while every other device signs out.
      const current = getSession();
      if (current) {
        setSession({ ...current, accessToken: res.accessToken, refreshToken: res.refreshToken });
      }
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    },
  });
  const pwTooShort = newPw.length > 0 && newPw.length < 8;
  const pwMismatch = confirmPw.length > 0 && confirmPw !== newPw;
  const pwSame = currentPw.length > 0 && newPw.length > 0 && currentPw === newPw;
  const pwCanSubmit =
    currentPw.length > 0 && newPw.length >= 8 && confirmPw === newPw && !pwSame && !changePw.isPending;
  const changePwError = changePw.isError
    ? changePw.error instanceof ApiError && changePw.error.status === 400
      ? ((changePw.error.body as { details?: { code?: string } } | undefined)?.details?.code === "password_reuse"
          ? "Your new password must be different from your current password."
          : "Your current password is incorrect.")
      : apiErrorText(changePw.error, "Couldn't change your password. Please retry.")
    : null;

  const emailTrim = emailInput.trim();
  const emailValid = emailTrim === "" || EMAIL_RE.test(emailTrim);
  const sendDisabled = send.isPending || cooldown > 0 || !emailValid;

  const sendError = send.isError
    ? send.error instanceof ApiError && send.error.status === 429
      ? "Please wait about 30 seconds before requesting another code."
      : apiErrorText(send.error, "Couldn't send the code. Please retry.")
    : null;
  const confirmError = confirm.isError
    ? confirm.error instanceof ApiError && confirm.error.status === 400
      ? "That code is invalid or has expired — you have a limited number of attempts."
      : apiErrorText(confirm.error, "Couldn't confirm the code. Please retry.")
    : null;

  const sendLabel = send.isPending
    ? "Sending…"
    : cooldown > 0
      ? `Resend in ${cooldown}s`
      : sentTo !== null
        ? "Resend code"
        : "Send code";

  return (
    <section className="t1s-page t1s-narrow">
      <div className="page-h">
        <div>
          <h1>Security</h1>
          <div className="sub">Your own sign-in protection · email two-factor, a verified email and your password</div>
        </div>
      </div>

          <div className="card">
            <div className="card-h">
              <h3>Two-factor authentication</h3>
              {mfaOn ? <span className="pill v">Protected · email</span> : <span className="pill r">Not protected</span>}
            </div>
            <div className="card-b">
              <div className="t1s-switch-row">
                <div className="t1s-switch-text">
                  <div className="t1s-switch-title">Require an email code at sign-in</div>
                  <div className="t1s-switch-hint">
                    When on, we email a 6-digit code each time you sign in. It expires in 10 minutes.
                    Toggling here applies immediately.
                  </div>
                </div>
                <SettingSwitch
                  label="Require an email code at sign-in"
                  on={mfaOn}
                  disabled={mfaBusy}
                  onToggle={() => (mfaOn ? disableMfa.mutate() : enableMfa.mutate())}
                />
              </div>
              {mfaOn ? (
                <div className="t1s-protect on" role="status">
                  <ShieldCheck />
                  <span>
                    <b>Two-factor is on.</b>
                    Sign-ins to your account now need a code from your inbox.
                  </span>
                </div>
              ) : (
                <div className="t1s-protect off">
                  <Alert />
                  <span style={{ flex: 1 }}>
                    <b>Your account isn't protected by two-factor.</b>
                    Anyone with your password can sign in. Turning it on takes one click.
                  </span>
                  <button
                    type="button"
                    className="btn sm"
                    disabled={mfaBusy}
                    onClick={() => enableMfa.mutate()}
                  >
                    {enableMfa.isPending ? "Turning on…" : "Turn on"}
                  </button>
                </div>
              )}
              {mfaError && (
                <div className="banner crit mt-3">
                  <Alert />
                  <span>{mfaError}</span>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <h3>Preferred email</h3>
              {verifiedEmail ? <span className="pill v">Verified</span> : <span className="pill r">Not verified yet</span>}
            </div>
            <div className="card-b">
              <p className="mut sm t1s-help">
                Prove you control an address before it becomes your preferred email. Leave the field blank
                to verify the email already on your account.
              </p>

              {verifiedEmail && (
                <div className="banner info mb-3">
                  <CheckBig />
                  <div>
                    <b>{verifiedEmail}</b> is verified and set as your preferred email.
                  </div>
                </div>
              )}

              <div className="field mb-2.5">
                <label htmlFor="verify-email">Email to verify</label>
                <input
                  id="verify-email"
                  className="input"
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="you@company.com — or leave blank for your current email"
                />
              </div>
              {!emailValid && (
                <div className="mut sm mb-2.5 text-crit">
                  Enter a valid email address, or leave it blank.
                </div>
              )}
              {sendError && (
                <div className="banner crit mb-3">
                  <span>{sendError}</span>
                </div>
              )}
              <button className="btn primary" disabled={sendDisabled} onClick={() => send.mutate()}>
                {sendLabel}
              </button>

              {sentTo !== null && (
                <div className="mt-4 pt-3.5 border-t border-line">
                  <p className="mut sm t1s-help">
                    Code sent to <b>{sentTo || "your account email"}</b>. Enter it below — it expires in 10
                    minutes.
                  </p>
                  <div className="field mb-2.5">
                    <label htmlFor="verify-code">6-digit code</label>
                    <input
                      id="verify-code"
                      className="input"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="000000"
                    />
                  </div>
                  {confirmError && (
                    <div className="banner crit mb-3">
                      <span>{confirmError}</span>
                    </div>
                  )}
                  <button
                    className="btn primary"
                    disabled={code.length !== 6 || confirm.isPending}
                    onClick={() => confirm.mutate()}
                  >
                    {confirm.isPending ? "Confirming…" : "Confirm"}
                  </button>
                </div>
              )}
            </div>
          </div>

        <div className="card">
          <div className="card-h">
            <h3>Password</h3>
            <button
              type="button"
              className="btn sm ghost"
              aria-pressed={showPw}
              onClick={() => setShowPw((v) => !v)}
            >
              {showPw ? <EyeOff /> : <Eye />} {showPw ? "Hide passwords" : "Show passwords"}
            </button>
          </div>
          <div className="card-b">
            <p className="mut sm t1s-help">
              Changing your password signs out every other session — only this one stays signed in.
            </p>

            {changePw.isSuccess && (
              <div className="banner info mb-3">
                <CheckBig />
                <div>
                  <b>Password changed.</b> Every other session has been signed out.
                </div>
              </div>
            )}

            <div className="field mb-2.5">
              <label htmlFor="current-password">Current password</label>
              <input
                id="current-password"
                className="input"
                type={pwType}
                autoComplete="current-password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                onKeyDown={(e) => setCapsOn(e.getModifierState("CapsLock"))}
                onKeyUp={(e) => setCapsOn(e.getModifierState("CapsLock"))}
                onBlur={() => setCapsOn(false)}
                placeholder="Your current password"
              />
              {capsOn && (
                <p className="auth-hint mt-1.5 text-left text-warn" role="status">
                  Caps Lock is on.
                </p>
              )}
            </div>
            <div className="field mb-2.5">
              <label htmlFor="account-new-password">
                New password <span className="hint">— at least 8 characters</span>
              </label>
              <input
                id="account-new-password"
                className="input"
                type={pwType}
                autoComplete="new-password"
                minLength={8}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                onKeyDown={(e) => setCapsOn(e.getModifierState("CapsLock"))}
                onKeyUp={(e) => setCapsOn(e.getModifierState("CapsLock"))}
                onBlur={() => setCapsOn(false)}
                placeholder="At least 8 characters"
              />
              {capsOn && (
                <p className="auth-hint mt-1.5 text-left text-warn" role="status">
                  Caps Lock is on.
                </p>
              )}
              {pwTooShort && (
                <div className="mut sm mt-1.5 text-crit">
                  Use at least 8 characters.
                </div>
              )}
              {pwSame && !pwTooShort && (
                <div className="mut sm mt-1.5 text-crit">
                  Your new password must be different from your current password.
                </div>
              )}
            </div>
            <div className="field mb-2.5">
              <label htmlFor="account-confirm-password">Confirm new password</label>
              <input
                id="account-confirm-password"
                className="input"
                type={pwType}
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                onKeyDown={(e) => setCapsOn(e.getModifierState("CapsLock"))}
                onKeyUp={(e) => setCapsOn(e.getModifierState("CapsLock"))}
                onBlur={() => setCapsOn(false)}
                placeholder="Type the new password again"
              />
              {capsOn && (
                <p className="auth-hint mt-1.5 text-left text-warn" role="status">
                  Caps Lock is on.
                </p>
              )}
              {pwMismatch && (
                <div className="mut sm mt-1.5 text-crit">
                  Those passwords don't match.
                </div>
              )}
            </div>
            {changePwError && (
              <div className="banner crit mb-3">
                <span>{changePwError}</span>
              </div>
            )}
            <button className="btn primary" disabled={!pwCanSubmit} onClick={() => changePw.mutate()}>
              {changePw.isPending ? "Changing…" : "Change password"}
            </button>
          </div>
        </div>

      <div className="disclaimer">
        Codes are single-use, expire in 10 minutes, and are rate-limited. Two-factor and email
        verification are email-only — TalentHop never sends codes over SMS.
      </div>
    </section>
  );
}
